// Dashboard Tab — orchestrator. Heavy sub-sections live in ./sections and ./components
import { useState, useEffect, useCallback, useMemo } from "react";
import { Database } from "lucide-react";
import { API, COLORS, ORIENTATION_LABELS, FOUNDER_LABELS, MONETIZATION_LABELS } from "../../lib/constants";
import { WorldMapChart } from "./WorldMapChart";
import { DataGapsCard } from "./DataGapsCard";
import { AICCIBanner } from "./AICCIBanner";
import { getKeyInsights } from "./lib/getKeyInsights";
import { SOURCE_COLORS_MAP, GOVERNANCE_COLORS } from "./lib/chartTokens";

import { GalaxyHero } from "./sections/GalaxyHero";
import { KeyFindingBanner } from "./sections/KeyFindingBanner";
import { YearFilterBar } from "./sections/YearFilterBar";
import { StatsOverviewGrid } from "./sections/StatsOverviewGrid";
import { InclusionInsightCard } from "./sections/InclusionInsightCard";
import { OrientationCharts } from "./sections/OrientationCharts";
import { CrossTabChart } from "./sections/CrossTabChart";
import { GeographicDistribution } from "./sections/GeographicDistribution";
import { TrendMonetizationRow } from "./sections/TrendMonetizationRow";
import { DatasetComposition } from "./sections/DatasetComposition";

import { FieldDistributionChart } from "./components/FieldDistributionChart";
import { WhatChangedTicker } from "./components/WhatChangedTicker";

export const DashboardTab = ({ stats, researchStats }) => {
  const [showUSA, setShowUSA] = useState(true);
  const [newThisWeek, setNewThisWeek] = useState(0);
  const [newApps, setNewApps] = useState(0);
  const [newDevices, setNewDevices] = useState(0);
  const [dynamicData, setDynamicData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [filteredStats, setFilteredStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/stats/dynamic`).then((r) => r.json()).then(setDynamicData).catch(() => {});
    fetch(`${API}/mirror/new-this-week`)
      .then((r) => r.json())
      .then((d) => {
        setNewThisWeek(d.new_this_week || 0);
        setNewApps(d.new_apps || 0);
        setNewDevices(d.new_devices || 0);
      })
      .catch(() => {});
  }, []);

  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
    if (!year) {
      setFilteredStats(null);
      return;
    }
    fetch(`${API}/stats/dynamic?year_from=${year}&year_to=${year}`)
      .then((r) => r.json())
      .then((d) => setFilteredStats(d.filtered_stats))
      .catch(() => {});
  }, []);

  const displayStats = filteredStats || {
    total: stats?.total_apps || 0,
    orientation: stats?.orientation_distribution || {},
    founders: stats?.founder_distribution || {},
    num_countries: stats?.num_countries || 0,
  };

  const insights = useMemo(() => getKeyInsights(stats, researchStats), [stats, researchStats]);

  // ── Chart data transformations ──
  const orientationUnknown = stats?.orientation_distribution?.unknown || 0;
  const orientationData = useMemo(() => stats ? Object.entries(stats.orientation_distribution || {})
    .filter(([key]) => key !== "unknown")
    .map(([key, value]) => ({
      name: ORIENTATION_LABELS[key] || key,
      value,
      fill: COLORS[key] || COLORS.unknown,
    })) : [], [stats]);

  const founderUnknown = stats?.founder_distribution?.unknown || 0;
  const founderData = useMemo(() => stats ? Object.entries(stats.founder_distribution || {})
    .filter(([key]) => key !== "unknown")
    .map(([key, value]) => ({
      name: FOUNDER_LABELS[key] || key,
      value,
      fill: COLORS[key] || COLORS.unknown,
    })) : [], [stats]);

  const crossTabData = useMemo(() => stats ? Object.values(
    stats.cross_tabulation?.reduce((acc, item) => {
      const founder = item.founder || "unknown";
      if (founder === "unknown") return acc;
      if (!acc[founder]) {
        acc[founder] = { founder: FOUNDER_LABELS[founder] || founder, care: 0, utility: 0, creative: 0, extraction: 0 };
      }
      const orient = item.orientation || "unknown";
      if (orient !== "unknown") acc[founder][orient] = item.count;
      return acc;
    }, {}) || {}
  ) : [], [stats]);

  const trendData = useMemo(() => stats?.monthly_trend?.map((item) => ({
    month: item.month,
    count: item.count,
  })) || [], [stats]);

  const countryUnknown = stats?.country_distribution?.Unknown || 0;
  const allCountryData = useMemo(() => stats?.country_distribution
    ? Object.entries(stats.country_distribution)
        .filter(([country]) => country !== "Unknown")
        .map(([country, count]) => ({ country, count }))
    : [], [stats]);
  const nonUSCountryData = useMemo(() => stats?.country_distribution
    ? Object.entries(stats.country_distribution)
        .filter(([country]) => country !== "USA" && country !== "Unknown")
        .map(([country, count]) => ({ country, count }))
    : [], [stats]);
  const countryData = showUSA ? allCountryData : nonUSCountryData;

  const monetizationData = useMemo(() => stats?.monetization_distribution
    ? Object.entries(stats.monetization_distribution)
        .filter(([key]) => key !== "unknown")
        .map(([key, value]) => ({
          name: MONETIZATION_LABELS[key] || key,
          value,
          fill:
            key === "subscription" ? "#f59e0b" :
            key === "freemium" ? "#10b981" :
            key === "enterprise" ? "#8b5cf6" :
            key === "usage_based" ? "#3b82f6" :
            key === "free" ? "#22c55e" :
            key === "ads" ? "#ef4444" : "#6b7280",
        }))
    : [], [stats]);

  const sourceData = useMemo(() => stats?.source_distribution
    ? Object.entries(stats.source_distribution)
        .sort(([, a], [, b]) => b - a)
        .map(([key, value]) => ({
          name: key,
          value,
          fill: SOURCE_COLORS_MAP[key] || "#6b7280",
        }))
    : [], [stats]);

  const governanceData = useMemo(() => stats?.governance_distribution
    ? Object.entries(stats.governance_distribution)
        .filter(([key]) => key !== "unknown")
        .map(([key, value]) => {
          const label =
            key === "system_aware" ? "System-Aware" :
            key === "market_first" ? "Market-First" :
            key === "participatory" ? "Participatory" :
            key === "neutral" ? "Neutral" : key;
          return { name: label, value, fill: GOVERNANCE_COLORS[key] || "#6b7280" };
        })
    : [], [stats]);

  const careApps = stats?.orientation_distribution?.care || 0;
  const totalApps = stats?.total_apps || 0;
  const carePercent = totalApps > 0 ? ((careApps / totalApps) * 100).toFixed(1) : "0";

  return (
    <div className="space-y-6 animate-fade-in">
      <GalaxyHero totalApps={totalApps} />

      <div id="observatory-content">
        {(!stats || stats.total_apps === 0) && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 text-center" data-testid="seeding-indicator">
            <div className="animate-pulse mb-2">
              <Database className="w-8 h-8 mx-auto text-blue-400" />
            </div>
            <p className="text-blue-300 font-medium">Loading dataset — first load may take a moment</p>
            <p className="text-blue-400/60 text-sm mt-1">66,000+ AI apps being prepared...</p>
          </div>
        )}

        {newThisWeek > 0 && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-xl" data-testid="new-this-week">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-semibold text-emerald-400">
              {newThisWeek.toLocaleString()} new {newThisWeek > 1 ? "entities" : "entity"} added this week
              {newApps > 0 && newDevices > 0 && <span className="text-emerald-400/60"> ({newApps} apps + {newDevices} devices)</span>}
              {newApps > 0 && !newDevices && <span className="text-emerald-400/60"> ({newApps} apps)</span>}
              {!newApps && newDevices > 0 && <span className="text-emerald-400/60"> ({newDevices} devices)</span>}
            </span>
          </div>
        )}

        <AICCIBanner entityType="application" />

        <KeyFindingBanner careApps={careApps} carePercent={carePercent} insights={insights} />

        <YearFilterBar
          years={dynamicData?.available_years}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
        />

        {!selectedYear && dynamicData?.what_changed?.length > 0 && (
          <WhatChangedTicker changes={dynamicData.what_changed} />
        )}

        <StatsOverviewGrid
          displayStats={displayStats}
          selectedYear={selectedYear}
          sparklines={dynamicData?.sparklines}
        />

        <InclusionInsightCard stats={stats} />

        <OrientationCharts
          orientationData={orientationData}
          founderData={founderData}
          orientationUnknown={orientationUnknown}
          founderUnknown={founderUnknown}
          totalApps={totalApps}
        />

        <CrossTabChart data={crossTabData} />

        <GeographicDistribution
          countryData={countryData}
          totalApps={totalApps}
          numCountries={stats?.num_countries}
          countryUnknown={countryUnknown}
          showUSA={showUSA}
          onToggleUSA={() => setShowUSA((v) => !v)}
        />

        <WorldMapChart countryDistribution={stats?.country_distribution} />

        <FieldDistributionChart />

        <TrendMonetizationRow trendData={trendData} monetizationData={monetizationData} />

        <DataGapsCard />

        <DatasetComposition
          sourceData={sourceData}
          governanceData={governanceData}
          totalApps={totalApps}
        />
      </div>
    </div>
  );
};

export default DashboardTab;
